import { NATIVE_TOKEN_ADDRESS } from '@cronos-app/sdk';
import type { TSignature } from '@ui/locales/config';
import {
  validateMaxDecimals,
  validateMaxValue,
  validateMinValue,
  validateNotEmpty,
  validateNotZero,
  validateNumberFormat,
} from '@ui/utils/validations';
import { Fraction } from 'bi-fraction';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo } from 'react';

import {
  MINIMUM_INPUT_BY_TOKEN,
  MINIMUM_OUTPUT_BY_TOKEN,
} from '../configs/token';
import { useCardContextStore } from '../useCardContextStore';
import { useSwapAndTopUpInfo, useTokenUsdPriceByService } from '.';

export const useValidAmountStrAndSwapAndTopUpInfo = () => {
  const { amount, token } = useCardContextStore();
  const validAmountStr = useMemo(() => {
    try {
      return new Fraction(amount).toFixed(token.decimals);
    } catch (err) {
      //
    }
  }, [amount, token]);
  const { data: swapAndTopUpInfo } = useSwapAndTopUpInfo(
    token,
    validAmountStr ?? '0',
  );
  return {
    validAmountStr,
    swapAndTopUpInfo,
  };
};

const NATIVE_MIN_AMOUNT = 3; // 3 zkCRO

export const useMaxValue = (balance: Fraction | undefined) => {
  const token = useCardContextStore((s) => s.token);
  if (token.address === NATIVE_TOKEN_ADDRESS) {
    const maxValue = balance?.sub(NATIVE_MIN_AMOUNT);
    if (!maxValue || maxValue.lt(0)) {
      return Fraction.ZERO;
    }
    return maxValue;
  }
  return balance;
};

export const useUpdateDepositAmountInputError = (
  maxDecimals: number,
  amount: string,
  balance?: Fraction,
) => {
  const t = useTranslations();
  const { validAmountStr, swapAndTopUpInfo } =
    useValidAmountStrAndSwapAndTopUpInfo();
  const { token } = useCardContextStore();
  const fromTokenPrice = useTokenUsdPriceByService(token);
  const toToken = swapAndTopUpInfo?.toToken;
  const toAmount = swapAndTopUpInfo?.toAmount;
  useEffect(() => {
    const validate = async () => {
      if (
        fromTokenPrice &&
        validAmountStr &&
        new Fraction(fromTokenPrice).mul(validAmountStr).gt(10000)
      )
        return t('card.Maximum top-up is $10,000 per transaction');
      const minInput = MINIMUM_INPUT_BY_TOKEN[token.symbol];
      const minOutput =
        toToken === undefined
          ? undefined
          : MINIMUM_OUTPUT_BY_TOKEN[toToken.symbol];
      if (
        validAmountStr &&
        minInput &&
        new Fraction(validAmountStr).lt(minInput)
      )
        return t('card.Minimum top-up is {amount} {symbol}', {
          amount: minInput,
          symbol: token.symbol,
        });

      if (
        toToken !== undefined &&
        toAmount &&
        minOutput &&
        new Fraction(toAmount).lt(minOutput)
      )
        return t('card.Minimum {symbol} received is {amount}', {
          amount: minOutput,
          symbol: toToken.symbol,
        });
      return basicValidators(t, maxDecimals, amount, balance);
    };
    validate().then((error) =>
      useCardContextStore.getState().setInputError(error ? error : undefined),
    );
  }, [
    amount,
    balance,
    fromTokenPrice,
    maxDecimals,
    t,
    toAmount,
    toToken,
    token,
    validAmountStr,
  ]);
};

const basicValidators = (
  t: TSignature,
  maxDecimals: number,
  amount: string,
  balance?: Fraction,
) =>
  validateNotEmpty({
    t,
    value: amount,
  }) ||
  validateNumberFormat({
    t,
    value: amount,
  }) ||
  validateNotZero({
    t,
    value: amount,
  }) ||
  validateMinValue({
    t,
    value: amount,
    minValue: 0,
  }) ||
  validateMaxValue({
    t,
    value: amount,
    maxValue: balance,
  }) ||
  validateMaxDecimals({
    t,
    value: amount,
    maxDecimals: maxDecimals,
  });
